import type { RouteRecordRaw } from 'vue-router'
import THEMES from '@/themes/config'

const isOptionalSegment = (segment: string) => {
  return segment.startsWith('[[') && segment.endsWith(']]')
}

const isDynamicSegment = (segment: string) => {
  return segment.startsWith('[') && segment.endsWith(']')
}

// 是否存在可跳过路由段，如 _type
const isSkipSegment = (segment: string) => {
  return segment.startsWith('_') && !segment.startsWith('__')
}

// 是否隐藏路由，如 __type
const isHiddenRoute = (segments: string[]) => {
  return segments[segments.length - 1]?.startsWith('__')
}

const processRoutePath = (pathSegments: string[]) => {
  return pathSegments.map(segment => {
    // 处理可选路由段，如 [[type]] -> :type?
    if (isOptionalSegment(segment)) {
      return ':' + segment.slice(2, -2) + '?'
    }
    // 处理动态路由段，如 [type] -> :type
    if (isDynamicSegment(segment)) {
      return ':' + segment.slice(1, -1)
    }
    return segment
  }).join('/')
}

export default async (theme: string, skin: string) => {
  const [themeModule] = await Promise.all([
    THEMES[theme].getTheme(),
    THEMES[theme].skins[skin]()
  ])

  return genRoutes(themeModule.default, theme)
}

export const genRoutes = async (themeModule: any, theme: string) => {
  const { viewModules, metaModules } = themeModule

  const routes: RouteRecordRaw[] = []

  const routeMap = new Map<string, RouteRecordRaw>()
  const tmpRouteMap = new Map<string, RouteRecordRaw>()

  try {
    for (const [path, component] of Object.entries(viewModules)) {
      const pathSegments = path
        .split('views')[1]
        .split('/')
        .map(s => s.toLowerCase())
        .filter(Boolean)

      const routeSegments = pathSegments.filter(s => !isSkipSegment(s)).slice(0, -1)
      const routePath = processRoutePath(routeSegments)
      const routeName = routeSegments[routeSegments.length - 1]
      if (!routeName) continue

      if (isHiddenRoute(routeSegments)) continue

      // @ts-ignore
      const route: RouteRecordRaw = {
        path: `/${routePath}`,
        name: routeName,
        component: component as RouteRecordRaw['component'],
        // children: []
      }

      let isOptional = routeSegments.some(isOptionalSegment)
      let isDynamic = routeSegments.some(isDynamicSegment)
      // let isSkip = routeSegments.some(isSkipSegment)
      if (isOptional || isDynamic) {
        // 取非可选的name
        route.name =  [...routeSegments].reverse().find(s => !isOptionalSegment(s))
      }

      let isStandalone = false
      let isRewriteRoute = false
      let isHidden = false

      const metaPath = path.replace('index.vue', 'meta.js')
      if (metaPath in metaModules) {
        try {
          const meta = await metaModules[metaPath]()
          route.meta = meta.default

          const metaData = meta.default
          if (metaData) {
            if (metaData.rewriteRoute) {
              Object.assign(route, metaData.rewriteRoute)
              // delete metaData.rewriteRoute
              isRewriteRoute = true
            }

            isStandalone = !!metaData.standalone
            isHidden = !!metaData.hidden
            
            const {include, exclude} = metaData

            if ((include && !include.includes(theme)) || (exclude && exclude.includes(theme))) {
              continue
            }
          }
        } catch (error) {
          console.error(`Failed to load meta for ${path}:`, error)
        }
      }

      if (isHidden) continue

      if (isRewriteRoute) {
        routes.push(route)
      } else {
        if (isStandalone || isOptional) {
          // 独立路由页面 || 可选路由
          route.path = `/${routePath}`
          routes.push(route)
        } else if (routeName == routePath) {
          // 顶级路由
          route.path = `/${routeName}`
          routes.push(route)
        } else {
          const parentPath = pathSegments.slice(0, -2).join('/')
          if (parentPath) {
            const parentRoute = routeMap.get(parentPath)
            if (parentRoute) {
              route.path = routeName // 子路由使用相对路径
              parentRoute.children = parentRoute.children || []
              parentRoute.children.push(route)
            } else {
              // 可能还没有注册
              tmpRouteMap.set(routePath, route)
            }
          }
        }

        routeMap.set(routePath, route)
      }
    }

    let i = 0
    const maxAttempts = tmpRouteMap.size * 2
    while (tmpRouteMap.size > 0) {
      if (i++ > maxAttempts) {
        throw new Error('route init timeout')
      }

      let resolvedAny = false
      for (const [path, route] of tmpRouteMap.entries()) {
        const parentPath = path.split('/').slice(0, -1).join('/')
        const parentRoute = routeMap.get(parentPath)

        if (parentRoute) {
          parentRoute.children = parentRoute.children || []
          parentRoute.children.push(route)
          tmpRouteMap.delete(path)
          resolvedAny = true
        }
      }

      if (!resolvedAny) {
        // throw new Error('route init timeout')
        console.error('failed to resolve routes:', Array.from(tmpRouteMap.keys()))
        break
      }
    }
    
    routes.reduce((acc, route) => {
      if (route.name) {
        if (acc.includes(route.name as string)) {
          console.warn('duplicate route.name:', route.name)
        } else {
          acc.push(route.name as string)
        }
      }

      return acc
    }, [] as string[])
  } catch (error) {
    console.error('Error generating routes:', error)
  }
  // console.log('routes', routes)

  return routes
}

